import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import { styled, useTheme, type Theme } from '@classify/react-core-components/styles';
import Typography, { Props as TypographyProps } from '@classify/react-core-components/Typography';
import Card from '@classify/react-core-components/Card';
import { alpha } from '@mui/system';
import Box from '@classify/react-core-components/Box';
import OverlayWrapper from '../BasePathItem/OverlayWrapper';
export interface Props {
  variant?: 'default' | 'compact';
  hasForcedRowLayout?: boolean;
  thumbnail: string;
  fieldColor: string;
  field: string;
  title: string;
  description?: string;
  children?: ReactNode;
  footer?: ReactNode;
  link?: string | null;
  overlay?: ReactNode;
  titleProps?: TypographyProps;
}
const rootStyles = ({
  theme: {
    shadows
  }
}: {
  theme: Theme;
}) => ({
  display: 'block',
  textDecoration: 'none',
  '&[href] > :hover': {
    boxShadow: shadows.containedInteractionRaised
  }
});
const Root = styled('div')(rootStyles);
const LinkRoot = styled('a')(rootStyles);
const PathCard = styled(Card<typeof Card>, {
  shouldForwardProp: prop => !['hasForcedRowLayout', 'hasFooter', 'isCompact'].includes(prop as string)
})<{
  hasForcedRowLayout: boolean;
  hasFooter: boolean;
  isCompact: boolean;
}>(({
  theme: {
    breakpoints
  },
  hasForcedRowLayout,
  hasFooter,
  isCompact
}) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  ...(hasFooter && {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    borderBottom: 'none'
  }),
  [breakpoints.up('sm')]: isCompact ? {
    flexDirection: 'row',
    alignItems: 'center'
  } : {
    flexDirection: hasForcedRowLayout ? 'row' : 'column',
    maxWidth: hasForcedRowLayout && 'unset'
  },
  [breakpoints.up('md')]: {
    maxWidth: hasForcedRowLayout || isCompact ? 'unset' : 704,
    flexDirection: 'row'
  },
  [breakpoints.up('lg')]: {
    maxWidth: 'unset'
  }
}));
const PathInfoContainer = styled('div', {
  shouldForwardProp: prop => prop !== 'isCompact'
})<{
  isCompact: boolean;
}>(({
  theme: {
    spacing
  },
  isCompact
}) => ({
  padding: spacing(2),
  display: 'flex',
  flexDirection: 'column',
  alignContent: 'flex-start',
  ...(isCompact && {
    flexGrow: 1,
    minWidth: 0
  })
}));
const ChildrenWrapper = styled('div')(({
  theme: {
    spacing
  }
}) => ({
  width: '100%',
  flexGrow: 1,
  display: 'flex',
  alignItems: 'flex-end',
  marginTop: spacing(1)
}));
const PathIllustration = styled(Box, {
  shouldForwardProp: prop => !['hasForcedRowLayout', 'isCompact'].includes(prop as string)
})<{
  hasForcedRowLayout: boolean;
  isCompact: boolean;
}>(({
  theme: {
    breakpoints
  },
  hasForcedRowLayout,
  isCompact
}) => ({
  position: 'relative',
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'contain',
  backgroundPosition: 'bottom',
  ...(isCompact ? {
    flexShrink: 0,
    height: 170,
    [breakpoints.up('sm')]: {
      width: 205,
      height: 'auto',
      aspectRatio: '205 / 139'
    }
  } : {
    minHeight: 104,
    [breakpoints.up('sm')]: {
      minWidth: 176,
      minHeight: hasForcedRowLayout ? 'unset' : 168
    },
    [breakpoints.up('md')]: {
      width: 288,
      minHeight: 168
    }
  })
}));
const FooterWrapper = styled('div')(({
  theme: {
    radius,
    palette
  }
}) => ({
  borderRadius: `0 0 ${radius.surfaceCoreSmall} ${radius.surfaceCoreSmall}`,
  border: `1px solid ${palette.layout.surface.border}`,
  borderTop: 'none',
  overflow: 'hidden'
}));
const PathDiscoveryItem = t0 => {
  const $ = _c(61);
  let description;
  let field;
  let fieldColor;
  let otherProps;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let thumbnail;
  let title;
  let titleProps;
  if ($[0] !== t0) {
    ({
      variant: t1,
      hasForcedRowLayout: t2,
      thumbnail,
      fieldColor,
      field,
      title,
      description,
      children: t3,
      footer: t4,
      link: t5,
      overlay: t6,
      titleProps,
      ...otherProps
    } = t0);
    $[0] = t0;
    $[1] = description;
    $[2] = field;
    $[3] = fieldColor;
    $[4] = otherProps;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
    $[11] = thumbnail;
    $[12] = title;
    $[13] = titleProps;
  } else {
    description = $[1];
    field = $[2];
    fieldColor = $[3];
    otherProps = $[4];
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
    t6 = $[10];
    thumbnail = $[11];
    title = $[12];
    titleProps = $[13];
  }
  const variant = t1 === undefined ? "default" : t1;
  const hasForcedRowLayout = t2 === undefined ? false : t2;
  const children = t3 === undefined ? null : t3;
  const footer = t4 === undefined ? null : t4;
  const link = t5 === undefined ? null : t5;
  const overlay = t6 === undefined ? null : t6;
  const {
    palette
  } = useTheme();
  const RootComponent = link ? LinkRoot : Root;
  const isCompact = variant === "compact";
  let t7;
  if ($[14] !== link) {
    t7 = link ? {
      href: link
    } : {};
    $[14] = link;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const t8 = Boolean(footer);
  const t9 = `url(${thumbnail})`;
  const t10 = overlay ? palette.notification.neutral.main : fieldColor;
  let t11;
  if ($[16] !== t10) {
    t11 = alpha(t10, 0.32);
    $[16] = t10;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t11 || $[19] !== t9) {
    t12 = {
      backgroundImage: t9,
      backgroundColor: t11
    };
    $[18] = t11;
    $[19] = t9;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== overlay) {
    t13 = overlay && <OverlayWrapper component="div" variant="subheadingSmall">{overlay}</OverlayWrapper>;
    $[21] = overlay;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== hasForcedRowLayout || $[24] !== isCompact || $[25] !== t12 || $[26] !== t13) {
    t14 = <PathIllustration hasForcedRowLayout={hasForcedRowLayout} isCompact={isCompact} data-testid="path-discovery-illustration" sx={t12}>{t13}</PathIllustration>;
    $[23] = hasForcedRowLayout;
    $[24] = isCompact;
    $[25] = t12;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const t15 = overlay ? palette.text.onMainDisabled : fieldColor;
  let t16;
  if ($[28] !== t15) {
    t16 = {
      color: t15
    };
    $[28] = t15;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== field || $[31] !== t16) {
    t17 = <Typography variant="overline" sx={t16}>{field}</Typography>;
    $[30] = field;
    $[31] = t16;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      mb: 1
    };
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== title || $[35] !== titleProps) {
    t19 = <Typography variant="h5" component="span" sx={t18} {...titleProps}>{title}</Typography>;
    $[34] = title;
    $[35] = titleProps;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== description) {
    t20 = description && <Typography variant="bodySmall" sx={{
      display: "inline-block",
      maxWidth: 768
    }}>{description}</Typography>;
    $[37] = description;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== children) {
    t21 = children && <ChildrenWrapper>{children}</ChildrenWrapper>;
    $[39] = children;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== isCompact || $[42] !== t17 || $[43] !== t19 || $[44] !== t20 || $[45] !== t21) {
    t22 = <PathInfoContainer isCompact={isCompact}>{t17}{t19}{t20}{t21}</PathInfoContainer>;
    $[41] = isCompact;
    $[42] = t17;
    $[43] = t19;
    $[44] = t20;
    $[45] = t21;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] !== hasForcedRowLayout || $[48] !== isCompact || $[49] !== t14 || $[50] !== t22 || $[51] !== t8) {
    t23 = <PathCard hasForcedRowLayout={hasForcedRowLayout} hasFooter={t8} isCompact={isCompact} data-testid="path-discovery-card">{t14}{t22}</PathCard>;
    $[47] = hasForcedRowLayout;
    $[48] = isCompact;
    $[49] = t14;
    $[50] = t22;
    $[51] = t8;
    $[52] = t23;
  } else {
    t23 = $[52];
  }
  let t24;
  if ($[53] !== footer) {
    t24 = footer && <FooterWrapper>{footer}</FooterWrapper>;
    $[53] = footer;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  let t25;
  if ($[55] !== RootComponent || $[56] !== otherProps || $[57] !== t23 || $[58] !== t24 || $[59] !== t7) {
    t25 = <RootComponent {...t7} data-testid="path-discovery-item" {...otherProps}>{t23}{t24}</RootComponent>;
    $[55] = RootComponent;
    $[56] = otherProps;
    $[57] = t23;
    $[58] = t24;
    $[59] = t7;
    $[60] = t25;
  } else {
    t25 = $[60];
  }
  return t25;
};
export default PathDiscoveryItem;